"use client";
import { AppBar, Box, CircularProgress, Typography } from "@mui/material";
import { useCommentsStore } from "@/stores/commentsStore";
import CommentItem from "@/components/comments/CommentItem";
import { useCallback, useEffect, useRef } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useD3Fetch } from "@/hook/useD3Fetch";
import { Comment, CommentsPageResponse, IComments } from "@/types/CommentsTypes";
import { IUsers } from "@/types";
import { safeT } from "@/utils/safeT";
import { useTranslations } from "next-intl";

export default function CommentList({
  lang,
  debateId,
  debateUser,
}: {
  lang: string;
  debateId: string;
  debateUser: IUsers;
}) {
  const { inputHeight, sortOption, limit, refreshTrigger } = useCommentsStore();

  const observer = useRef<IntersectionObserver | null>(null);
  const d3Fetch = useD3Fetch();
  const queryClient = useQueryClient();
  const t = useTranslations();

  // 댓글 가져오기
  const fetchComments = async ({ pageParam = 0 }: { pageParam: number }) => {
    // 실제 API 엔드포인트로 대체
    const { data } = await d3Fetch.get<CommentsPageResponse>(
      `/comments/list/${lang}/${debateId}/${sortOption}/${pageParam}/${limit}`
    );
    return {
      comments: data?.comments,
      nextPage: data?.hasMore ? data.nextPage : undefined,
      totalElements: data?.totalElements,
      limit: data?.limit,
      sort: data?.sort,
    };
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ["comments", debateId, sortOption], // 캐시 구분을 위해 포함
    queryFn: fetchComments,
    getNextPageParam: (lastPage) => {
      // 동적 매개변수(pageParam)만 반환
      return lastPage.nextPage;
    },
    initialPageParam: 0,
  });
  // 페이지별 댓글 평탄화
  const comments = data?.pages.flatMap((page) => page.comments) ?? [];

  const lastCommentRef = useCallback(
    (node: HTMLDivElement) => {
      if (isFetchingNextPage || isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);

      return () => {
        observer.current?.disconnect();
      };
    },
    [isFetchingNextPage, isLoading, hasNextPage, fetchNextPage]
  );

  // 좋아요/싫어요 업데이트 Mutation
  const updateCommentMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "like" | "dislike" }) => {
      // 실제 API 호출로 대체
      const response = await fetch(`/api/comments/${id}/${action}`, { method: "POST" });
      return response.json();
    },
    onSuccess: (updatedComment) => {
      // 캐시 업데이트
      queryClient.setQueryData(["comments", sortOption, debateId], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            comments: page.comments.map((comment: IComments) =>
              comment.id === updatedComment.id ? updatedComment : comment
            ),
          })),
        };
      });
    },
  });

  useEffect(() => {
    // 해당 queryKey를 refetch (무한스크롤이므로 전체 refetch보다는 invalidateQueries 권장)
    queryClient.invalidateQueries({
      queryKey: ["comments", debateId],
    });
  }, [refreshTrigger, queryClient, sortOption, debateId]);

  const NoCommentsMessage = () => {
    return (
      <>
        <Typography variant="h5" textAlign="center" lineHeight={3}>
          {safeT(t, "ui.message.noDebate", {
            defaultMessage: "아직 댓글이 없습니다.",
          })}
        </Typography>
        <Typography variant="body1" textAlign="center">
          {" "}
          {safeT(t, "ui.message.startDiscussion", {
            defaultMessage: "💬 당신의 한 마디가 토론을 시작합니다.",
          })}
        </Typography>
      </>
    );
  };
  return (
    <>
      <Box sx={{ paddingBottom: `100px` }}>
        {comments.map((item, index) => (
          <CommentItem
            ref={index === comments.length - 1 ? lastCommentRef : null}
            key={item?.comment.id}
            comment={item as Comment}
            lang={lang}
            debateUser={debateUser}
          />
        ))}

        {!hasNextPage && !isLoading && comments.length === 0 && <NoCommentsMessage />}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <CircularProgress size={24} color="primary" />
          </Box>
        )}
        {!hasNextPage && comments.length > 0 && (
          <Typography textAlign="center">
            {safeT(t, "ui.message.noMoreComments", {
              defaultMessage: "더 이상 댓글이 없습니다.",
            })}
          </Typography>
        )}
      </Box>
    </>
  );
}
